import { useCallback, useEffect, useRef } from 'react'

type DraggableProps = {
  onDragStart?: (event: MouseEvent | TouchEvent) => void
  onDragMove?: (event: MouseEvent | TouchEvent) => void
  onDragEnd?: (event: MouseEvent | TouchEvent) => void
}

function useDraggable<T extends HTMLElement>({ onDragStart, onDragMove, onDragEnd }: DraggableProps) {
  const draggingRef = useRef(false)
  const handleRef = useRef<T>(null)

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  const handleDragStart = useCallback(
    (event: MouseEvent | TouchEvent) => {
      draggingRef.current = true

      if (handleRef.current) {
        handleRef.current.style.cursor = document.body.style.cursor = 'grabbing'
      }

      if (onDragStart) {
        onDragStart(event)
      }
    },
    [onDragStart],
  )

  const handleDragMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (draggingRef.current && onDragMove) {
        onDragMove(event)
      }
    },
    [onDragMove],
  )

  const handleDragEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!draggingRef.current) {
        return
      }

      draggingRef.current = false

      if (onDragEnd) {
        onDragEnd(event)
      }

      if (handleRef.current) {
        handleRef.current.style.cursor = document.body.style.cursor = ''
      }
    },
    [onDragEnd],
  )

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    const currentHandleRef = handleRef.current

    currentHandleRef?.addEventListener('mousedown', handleDragStart)
    currentHandleRef?.addEventListener('touchstart', handleDragStart)

    return () => {
      currentHandleRef?.removeEventListener('mousedown', handleDragStart)
      currentHandleRef?.removeEventListener('touchstart', handleDragStart)
    }
  }, [handleRef.current, handleDragStart])

  useEffect(() => {
    // Add event listeners to window
    window.addEventListener('mousemove', handleDragMove)
    window.addEventListener('touchmove', handleDragMove)

    window.addEventListener('mouseup', handleDragEnd)
    window.addEventListener('touchend', handleDragEnd)

    return () => {
      // Unregister event listeners from window
      window.removeEventListener('mousemove', handleDragMove)
      window.removeEventListener('touchmove', handleDragMove)

      window.removeEventListener('mouseup', handleDragEnd)
      window.removeEventListener('touchend', handleDragEnd)
    }
  })

  return {
    handleRef,
  }
}

export { useDraggable }
